import QuizInteractiveFlow from "@/components/storyboard/quiz/QuizInteractiveFlow";
import QuizTrueFalse, {
  type QuizTrueFalseState,
} from "@/components/storyboard/quiz/QuizTrueFalse";
import { useQuizValidations } from "@/hooks/useQuizValidations";
import { InteractiveElementStatusKind } from "@/lib/interactiveElementState";
import type { QuizTrueFalseEntryData } from "@/types/storyboard";

export interface QuizTrueFalseFlowProps {
  id: string;
  title: string;
  entries: QuizTrueFalseEntryData[];
  onValidate: (answer: boolean, entryIndex: number) => Promise<boolean>;
}

/** True/False quiz in the shared interactive-element shell — inline in the
 * storyboard card.
 */
const QuizTrueFalseFlow = ({
  id,
  title,
  entries,
  onValidate,
}: QuizTrueFalseFlowProps) => {
  // Resume in-progress from the server: reconstruct the quiz state from the
  // per-entry answers already saved in validation_data, reopening at the first
  // unanswered question with the running correct count.
  const {
    status: statusOverride,
    isReady: validationsReady,
    refresh,
  } = useQuizValidations(
    id,
    entries.length,
    (answeredCount, correctCount): QuizTrueFalseState => ({
      currentIndex: answeredCount,
      selectedAnswer: null,
      isSubmitted: false,
      isCorrect: null,
      correctCount,
    })
  );
  // The override drives the card; this seeds the editor when there is no local
  // (same-device) progress to fall back on — e.g. resuming on another device.
  const serverResume =
    statusOverride?.kind === InteractiveElementStatusKind.InProgress
      ? (statusOverride.partial as QuizTrueFalseState)
      : undefined;

  return (
    <QuizInteractiveFlow<QuizTrueFalseState>
      id={id}
      title={title}
      illustrationKind="true_false"
      layout="inline"
      statusOverride={statusOverride}
      supportsInProgress
      reportProgress
      renderQuiz={({
        initialPartial,
        initialIndex,
        onPartialChange,
        onComplete,
      }) => {
        // QuizTrueFalse seeds from useState on first mount. Wait until local
        // in-progress exists or validation_data has settled so the initializer
        // sees the cross-device resume instead of starting at question 1.
        const seedReady = initialPartial != null || validationsReady;
        if (!seedReady) return null;

        return (
          <QuizTrueFalse
            entries={entries}
            onValidate={async (...args) => {
              const verdict = await onValidate(...args);
              refresh();
              return verdict;
            }}
            initialPartial={initialPartial ?? serverResume}
            initialIndex={initialIndex ?? serverResume?.currentIndex}
            onPartialChange={onPartialChange}
            onComplete={onComplete}
          />
        );
      }}
    />
  );
};

export default QuizTrueFalseFlow;
